import { useState } from "react";
import { toast } from "sonner";
import { submitEnquiry, apiErr } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../ui/dialog";

export const DemoDialog = ({ children, title = "Request a PARTECON Demo", intent = "demo" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", school: "", role: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, interest: title, source: intent });
      trackEvent(intent === "demo" ? "request_demo" : "talk_to_partecon", {
        source: intent,
        interest: title,
      });
      setOpen(false);
      setForm({ name: "", email: "", school: "", role: "", message: "" });
      toast.success("Thank you — the PARTECON team will reach out shortly.", {
        description: "Your enquiry has been received.",
      });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-[#9DA9BA]/60 focus:border-[#6366F1]/50 focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-[#0E1117] border border-white/10 text-white sm:max-w-lg" data-testid="demo-dialog">
        <DialogHeader>
          <span className="overline">PARTECON · {intent === "demo" ? "Pilot Enquiry" : "Get in Touch"}</span>
          <DialogTitle className="font-heading text-2xl font-medium tracking-tight mt-2">{title}</DialogTitle>
          <DialogDescription className="text-[#9DA9BA]">
            {intent === "demo"
              ? "Tell us about your school. We’ll set up a tailored walkthrough of the operating system."
              : "Tell us how you’d like to engage with PARTECON and our team will get in touch."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="demo-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="demo-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="demo-email" type="email" className={field} placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="demo-school" className={field} placeholder="School / organisation" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} />
            <div className="relative">
              <select data-testid="demo-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className={`${field} appearance-none pr-10 ${form.role ? "" : "text-[#9DA9BA]/60"}`}>
                <option value="" disabled className="bg-[#12141C]">Role of interest</option>
                {["Teacher", "Principal", "Administration", "Parent", "Student", "Other"].map((r) => (
                  <option key={r} value={r} className="bg-[#12141C] text-white">{r}</option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[#9DA9BA]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </div>
          </div>
          <textarea data-testid="demo-message" className={field} rows={3} placeholder="What would you like to achieve?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button type="submit" disabled={loading} data-testid="demo-submit" className="btn-primary w-full py-3 text-sm disabled:opacity-60">{loading ? "Submitting…" : "Submit Enquiry"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
